import { createSelector } from 'reselect';

import { RootModel } from '../models';
import {
  FloorTypes,
  JSONDataTypes,
  ProjectChildItemTypes,
  ProjectItemTypes,
  ProjectTypes,
  ShowroomTypes,
  StoreImageTypes,
  TourTypes,
  UserContactTypes,
} from '../../types';
import { formatNumberToSquareString, getFloorName } from '../../utils';
import {
  DIRECTION_TYPE,
  HOME_PROFILE_FEATURE_ACTIONS,
  HOME_PROFILE_PROMOTION,
  PROJECT_CHILD_ITEM_FURNITURE_STATUS,
  PROJECT_CHILD_ITEM_LAND_USE_RIGHT,
  PROJECT_CHILD_ITEM_POSITION_TYPE,
  PROJECT_CHILD_ITEM_RESIDENCE_TYPE,
  PROJECT_ITEM_TYPE,
} from '../../commons/constants';
import { FeatureActionTypes } from '../../commons/components/FeatureActions';
import { PromotionResponseTypes } from '../../types/response/promotion';
import { PromotionsTypes } from '../../types/response/promotion';
import { UserContactResponseTypes } from '../../types/response/userContact';

export const getHomeData = createSelector<
  RootModel,
  { [key: string]: ProjectChildItemTypes },
  number,
  ProjectChildItemTypes
>(
  (state) => state.projectChildItemEntity.ids,
  (state) => state.homeProfileModel.homeId,
  (ids, id) => ids[id]
);

export const getProjectChildItemInfo = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  { [key: string]: ProjectItemTypes },
  { [key: string]: FloorTypes },
  { [key: string]: ProjectChildItemTypes },
  number,
  number,
  number,
  number,
  JSONDataTypes[]
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectItemEntity.ids,
  (state) => state.floorEntity.ids,
  (state) => state.projectChildItemEntity.ids,
  (state) => state.projectModel.projectId,
  (state) => state.sitePlanModel.projectItemId,
  (state) => state.sitePlanModel.floorId,
  (state) => state.homeProfileModel.homeId,
  (
    projectIds,
    projectItemIds,
    floorIds,
    projectChildItemIds,
    projectId,
    projectItemId,
    floorId,
    homeId
  ) => {
    const project = projectIds[projectId];
    if (!project) return null;

    const projectItem = projectItemIds[projectItemId];
    if (!projectItem) return null;

    let floor: FloorTypes;
    if (projectItem.type === PROJECT_ITEM_TYPE.BUILDING) {
      floor = floorIds[floorId];
      if (!floor) return null;
    }

    const item = projectChildItemIds[homeId];
    if (!item) return null;

    let info: JSONDataTypes[] = [];

    if (item.residenceType && PROJECT_CHILD_ITEM_RESIDENCE_TYPE[item.residenceType]) {
      info.push({
        key: 'home-profile.residence-type.title',
        value: PROJECT_CHILD_ITEM_RESIDENCE_TYPE[item.residenceType].name,
        isLocale: true,
      });
    }

    if (item.square) {
      info.push({
        key: 'home-profile.total-square.title',
        value: formatNumberToSquareString(item.square),
      });
    }

    if (item.usableSquare) {
      info.push({
        key: 'home-profile.useable-square.title',
        value: formatNumberToSquareString(item.usableSquare),
      });
    }

    if (item.positionType && PROJECT_CHILD_ITEM_POSITION_TYPE[item.positionType]) {
      info.push({
        key: 'home-profile.position-type.title',
        value: PROJECT_CHILD_ITEM_POSITION_TYPE[item.positionType].label,
        isLocale: true,
      });
    }

    if (item.landUseRight && PROJECT_CHILD_ITEM_LAND_USE_RIGHT[item.landUseRight]) {
      info.push({
        key: 'home-profile.land-use-right.title',
        value: PROJECT_CHILD_ITEM_LAND_USE_RIGHT[item.landUseRight].name,
        isLocale: true,
      });
    }

    if (item.bedroom) {
      info.push({ key: 'home-profile.bedroom.title', value: `${item.bedroom}` });
    }

    if (item.bathroom) {
      info.push({ key: 'home-profile.bathroom.title', value: `${item.bathroom}` });
    }

    if (item.direction && DIRECTION_TYPE[item.direction]) {
      info.push({
        key: 'home-profile.direction-main-door-type.title',
        value: DIRECTION_TYPE[item.direction].name,
        isLocale: true,
      });
    }

    if (item.balconyDirections && !!item.balconyDirections.length) {
      const values = [];

      item.balconyDirections.forEach((direction) => {
        if (DIRECTION_TYPE[direction]) values.push(DIRECTION_TYPE[direction].name);
      });

      if (values.length) {
        info.push({
          key: 'home-profile.balcony-direction-type.title',
          value: '',
          values,
          isLocale: true,
        });
      }
    }

    if (item.parkingNum) {
      info.push({ key: 'home-profile.car-parking-num.title', value: `${item.parkingNum}` });
    }

    if (item.allowPet) {
      info.push({
        key: 'home-profile.allow-pet.title',
        value: 'home-profile.yes.title',
        isLocale: true,
      });
    }

    if (item.furnitureStatus && PROJECT_CHILD_ITEM_FURNITURE_STATUS[item.furnitureStatus]) {
      info.push({
        key: 'home-profile.furniture-status.title',
        value: PROJECT_CHILD_ITEM_FURNITURE_STATUS[item.furnitureStatus].name,
        isLocale: true,
      });
    }

    if (item.allowInstallment) {
      info.push({
        key: 'home-profile.allow-installment.title',
        value: 'home-profile.yes.title',
        isLocale: true,
      });
    }

    if (project.name) {
      info.push({ key: 'home-profile.project.title', value: project.name });
    }

    if (projectItem.name) {
      info.push({ key: 'home-profile.building.title', value: projectItem.name });
    }

    if (floor && floor.name) {
      info.push({ key: 'home-profile.floor.title', value: getFloorName(floor.name) });
    }

    if (item.shortInformation && !!item.shortInformation.length) {
      info = [...info, ...item.shortInformation.map((i) => ({ ...i, isLocale: false }))];
    }

    return info;
  }
);

export const getHomeProfileTreeData = createSelector<
  RootModel,
  { [key: string]: ProjectTypes },
  { [key: string]: ProjectItemTypes },
  { [key: string]: FloorTypes },
  { [key: string]: ProjectChildItemTypes },
  number,
  number,
  number,
  number,
  {
    project: ProjectTypes;
    projectItem: ProjectItemTypes;
    floor: FloorTypes;
    item: ProjectChildItemTypes;
  }
>(
  (state) => state.projectEntity.ids,
  (state) => state.projectItemEntity.ids,
  (state) => state.floorEntity.ids,
  (state) => state.projectChildItemEntity.ids,
  (state) => state.projectModel.projectId,
  (state) => state.sitePlanModel.projectItemId,
  (state) => state.sitePlanModel.floorId,
  (state) => state.homeProfileModel.homeId,
  (
    projectEntities,
    projectItemEntities,
    floorEntities,
    childItemEntities,
    projectId,
    projectItemId,
    floorId,
    homeId
  ) => ({
    project: projectEntities[projectId],
    projectItem: projectItemEntities[projectItemId],
    floor: floorEntities[floorId],
    item: childItemEntities[homeId],
  })
);

export const getPromotionHomeProfile = createSelector<
  RootModel,
  { [key: string]: PromotionResponseTypes },
  string[],
  {
    homeProfileButtons: PromotionsTypes[];
    homeProfileButtonBanners: PromotionsTypes[];
    homeProfileMobileBottoms: PromotionsTypes[];
    homeProfileMobileTops: PromotionsTypes[];
    homeProfilePopups: PromotionsTypes[];
    homeProfileTabletBottoms: PromotionsTypes[];
    homeProfileTabletTops: PromotionsTypes[];
    homeProfileDesktopBottoms: PromotionsTypes[];
    homeProfileDesktopTops: PromotionsTypes[];
    homeProfileRatingBanner: PromotionsTypes[];
    homeProfileInformationBanner: PromotionsTypes[];
    homeProfilePromotion: PromotionsTypes[];
    homeProfileFurnishingApartmentBanner: PromotionsTypes[];
    homeProfileProjectIntroBanner: PromotionsTypes[];
    homeProfileProjectLocationBanner: PromotionsTypes[];
    homeProfileLocalAmenitiesBanner: PromotionsTypes[];
  }
>(
  (state) => state.promotionEntity.ids,
  (state) => state.homeProfileModel.promotionCodes,
  (promotionEntities, promotionCodes) => {
    const dataPromotion = {
      homeProfileButtonBanners: [],
      homeProfileButtons: [],
      homeProfileMobileBottoms: [],
      homeProfileMobileTops: [],
      homeProfileTabletBottoms: [],
      homeProfileTabletTops: [],
      homeProfileDesktopBottoms: [],
      homeProfileDesktopTops: [],
      homeProfilePopups: [],
      homeProfileRatingBanner: [],
      homeProfileInformationBanner: [],
      homeProfilePromotion: [],
      homeProfileFurnishingApartmentBanner: [],
      homeProfileProjectIntroBanner: [],
      homeProfileProjectLocationBanner: [],
      homeProfileLocalAmenitiesBanner: [],
    };

    if (promotionCodes && promotionCodes.length) {
      promotionCodes.forEach((code) => {
        const promotion = promotionEntities[code];

        if (promotion) {
          const { displays } = promotion;

          Object.entries(displays).forEach(([key, value]) => {
            const promotions = value.map((item) => ({ ...item, parentCode: code }));
            const names = HOME_PROFILE_PROMOTION[key] && HOME_PROFILE_PROMOTION[key].nameArray;

            if (names) dataPromotion[names].push(...promotions);
          });
        }
      });
    }

    return dataPromotion;
  }
);

export const getPromotionDetail = createSelector<
  RootModel,
  { [key: string]: PromotionResponseTypes },
  { parentCode: string; code: string; type: string } | null,
  {
    id: number;
    code: string;
    type: string;
    title: string;
    image?: StoreImageTypes;
    description?: string;
    url?: string;
  }
>(
  (state) => state.promotionEntity.ids,
  (state) => state.homeProfileModel.promotionDetail,
  (promotionEntities, promotionDetails) => {
    if (!promotionDetails) return null;

    const { type, parentCode, code } = promotionDetails;
    const promotionDetail = promotionEntities[parentCode];
    const promotionTypeDetail = promotionDetail.displays[type];
    const promotionCodeDetail = promotionTypeDetail.find((e) => e.code === code);

    return promotionCodeDetail;
  }
);

// userContact
export const getUserContactData = createSelector<
  RootModel,
  { [key: string]: UserContactResponseTypes },
  string[],
  UserContactTypes[]
>(
  (state) => state.userContactEntity.ids,
  (state) => state.homeProfileModel.userContactCodes,
  (ids, codes) => {
    if (!ids) return null;
    const items: UserContactTypes[] = [];
    (codes || []).forEach((code) => {
      if (ids[code]) {
        items.push(ids[code]);
      }
    });
    return items;
  }
);

export const getHomeProfileFeatureActions = createSelector<
  RootModel,
  { [key: string]: ProjectChildItemTypes },
  ShowroomTypes[],
  number,
  FeatureActionTypes[]
>(
  (state) => state.projectChildItemEntity.ids,
  (state) => state.homeProfileModel.showroomData,
  (state) => state.homeProfileModel.homeId,
  (ids, showroomData, id) => {
    const item = ids[id];
    const actions = [];
    if (!item) return actions;

    if (item.media && !!item.media.length) {
      actions.push(HOME_PROFILE_FEATURE_ACTIONS.GALLERY);
    }

    if (item.virtualTourUrl) {
      actions.push(HOME_PROFILE_FEATURE_ACTIONS.VIEW_360);
    }

    if (item.furnitureUrl) {
      actions.push(HOME_PROFILE_FEATURE_ACTIONS.HOME_FURNITURE);
    }

    if (item.financeUrl) {
      actions.push(HOME_PROFILE_FEATURE_ACTIONS.FINANCE);
    }

    return actions;
  }
);

export const getHomeProfileVT360Data = createSelector<
  RootModel,
  { [key: string]: TourTypes },
  ShowroomTypes[],
  TourTypes[]
>(
  (state) => state.tourEntity.ids,
  (state) => state.homeProfileModel.showroomData,
  (ids, showroomData) => {
    if (!showroomData || !showroomData.length) return null;

    const items: TourTypes[] = [];
    const tours = Object.values(ids);

    showroomData.forEach((data) => {
      const item = tours.find((i) => i.uri === data.url);
      if (item) items.push(item);
    });

    return items;
  }
);
